import React, { useState, useEffect, useCallback } from 'react';
import { listWords, createWord, updateWord, deleteWord, Word } from '../repository/adminRepository';

const AdminPage: React.FC = () => {
  const [words, setWords] = useState<Word[]>([]);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<Word | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [formWord, setFormWord] = useState('');
  const [formEasy, setFormEasy] = useState(false);
  const [formNormal, setFormNormal] = useState(false);
  const [formDefinition, setFormDefinition] = useState('');
  const [formEtymology, setFormEtymology] = useState('');

  const loadWords = useCallback(async (append: boolean = false, lastKeyOverride?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const result = await listWords({
        limit: 50,
        last_key: append ? (lastKeyOverride ?? lastKey) : null,
        filter: filter || undefined,
        q: search || undefined,
      });
      if (append) {
        setWords((prev) => [...prev, ...result.words]);
      } else {
        setWords(result.words);
      }
      setLastKey(result.last_key);
    } catch (err: any) {
      setError(err.message || 'Failed to load words');
    } finally {
      setLoading(false);
    }
  }, [search, filter, lastKey]);

  useEffect(() => {
    loadWords(false);
  }, []); // initial load

  useEffect(() => {
    const timer = setTimeout(() => {
      loadWords(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]); // debounced search

  useEffect(() => {
    loadWords(false);
  }, [filter]); // reload on filter change

  const handleLoadMore = () => {
    loadWords(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setIsAdding(true);
    setFormWord('');
    setFormEasy(false);
    setFormNormal(false);
    setFormDefinition('');
    setFormEtymology('');
    setError(null);
  };

  const handleEdit = (word: Word) => {
    setIsAdding(false);
    setEditing(word);
    setFormWord(word.word);
    setFormEasy(word.is_easy_eligible);
    setFormNormal(word.is_normal_eligible);
    setFormDefinition(word.definition);
    setFormEtymology(word.etymology);
    setError(null);
  };

  const handleCancel = () => {
    setEditing(null);
    setIsAdding(false);
    setError(null);
  };

  const handleSave = async () => {
    setError(null);
    try {
      if (isAdding) {
        await createWord({
          word: formWord,
          is_easy_eligible: formEasy,
          is_normal_eligible: formNormal,
          definition: formDefinition,
          etymology: formEtymology,
        });
      } else if (editing) {
        await updateWord(editing.uuid, {
          word: formWord,
          is_easy_eligible: formEasy,
          is_normal_eligible: formNormal,
          definition: formDefinition,
          etymology: formEtymology,
        });
      }
      setEditing(null);
      setIsAdding(false);
      await loadWords(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save word');
    }
  };

  const handleDelete = async (word: Word) => {
    if (!window.confirm(`Delete "${word.word}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await deleteWord(word.uuid);
      await loadWords(false);
    } catch (err: any) {
      setError(err.message || 'Failed to delete word');
    }
  };

  const truncate = (s: string, max: number) => {
    if (s.length <= max) return s;
    return s.slice(0, max) + '...';
  };

  const cellStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderBottom: '1px solid var(--theme-border)',
    textAlign: 'left',
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      <a href="/" style={{ display: 'inline-block', marginBottom: 16, color: 'var(--theme-primary)' }}>
        &larr; Back to game
      </a>

      <div className="panel" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, flex: '1 1 auto' }}>Admin: Word Management</h2>
          <input
            type="text"
            placeholder="Search words..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--theme-border)', minWidth: 160 }}
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--theme-border)' }}
          >
            <option value="">All</option>
            <option value="easy">Easy</option>
            <option value="normal">Normal</option>
          </select>
          <button className="primary-button" onClick={handleAdd}>Add Word</button>
        </div>
      </div>

      {error && (
        <div className="notice error" style={{ marginBottom: 16 }}>{error}</div>
      )}

      <div className="panel" style={{ marginBottom: 24, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={cellStyle}>Word</th>
              <th style={cellStyle}>Easy</th>
              <th style={cellStyle}>Normal</th>
              <th style={cellStyle}>Definition</th>
              <th style={cellStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {words.map((w) => (
              <tr key={w.uuid}>
                <td style={{ ...cellStyle, textTransform: 'uppercase', fontWeight: 600 }}>{w.word}</td>
                <td style={cellStyle}>{w.is_easy_eligible ? '\u2713' : '\u2014'}</td>
                <td style={cellStyle}>{w.is_normal_eligible ? '\u2713' : '\u2014'}</td>
                <td style={cellStyle}>{truncate(w.definition, 60)}</td>
                <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                  <button className="secondary-button" style={{ marginRight: 8 }} onClick={() => handleEdit(w)}>Edit</button>
                  <button className="secondary-button" onClick={() => handleDelete(w)}>Delete</button>
                </td>
              </tr>
            ))}
            {words.length === 0 && !loading && (
              <tr>
                <td style={cellStyle} colSpan={5}>No words found.</td>
              </tr>
            )}
          </tbody>
        </table>
        {loading && <div style={{ padding: 12, textAlign: 'center' }}>Loading...</div>}
        {lastKey && !loading && (
          <div style={{ padding: 12, textAlign: 'center' }}>
            <button className="secondary-button" onClick={handleLoadMore}>Load more</button>
          </div>
        )}
      </div>

      {(editing || isAdding) && (
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>{isAdding ? 'Add Word' : 'Edit Word'}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Word</label>
              <input
                type="text"
                maxLength={5}
                value={formWord}
                onChange={(e) => setFormWord(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--theme-border)', width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={formEasy}
                  onChange={(e) => setFormEasy(e.target.checked)}
                />
                Easy eligible
              </label>
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={formNormal}
                  onChange={(e) => setFormNormal(e.target.checked)}
                />
                Normal eligible
              </label>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Definition</label>
              <textarea
                rows={3}
                value={formDefinition}
                onChange={(e) => setFormDefinition(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--theme-border)', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Etymology</label>
              <textarea
                rows={3}
                value={formEtymology}
                onChange={(e) => setFormEtymology(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--theme-border)', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="primary-button" onClick={handleSave}>Save</button>
              <button className="secondary-button" onClick={handleCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
